<script setup>

import {ref, nextTick, onMounted, computed, watch} from 'vue';
import DropDownItem from "@/classes/component-classes/DropDownItem";
import MovingTooltip from "@/components/common/other/MovingTooltip";
import Validation from "@/classes/global/Validation";
import DOMPurify from 'dompurify';

import {useRoute} from "vue-router";

import API from "@/classes/global/API";

const selectedItem = ref('');
const popupSelectResults = ref(props.popupResults);
const showResults = ref(false);
const selected = ref(-1);
const closeTimer = ref(null);
const closeTime = ref(600);
const localDataSource = ref([]);
const readOnly = ref(false);
const selectionMade = ref(false);
const valueNotValid = ref(false);
const menuFullOpenHeight = ref({type: Number, default: 0})

// HTML Refs
const inputRef = ref();
const selectRef = ref();
const ulRef = ref();

const route = useRoute();


const emits = defineEmits(['update:modelValue', 'update:validationFail', 'keydown', 'select', 'clear'])

const props = defineProps({

  modelValue: {},

  dataSource: {
    type: Array, default: function () {
      return []
    }
  },

  allowCustomValue: {type: Boolean, default: false},
  placeholder: {type: String, default: 'Select or Enter value... '},
  clearable: {type: Boolean, default: true},
  validationFail: {type: Object, default: new Validation()},
  validationText: {type: Array, default: ['Required']},
  popupResults: {type: Boolean, default: false},
  disable: {type: Boolean, default: false},
  width: {type: Number, default: 450},
  label: {type: String, default: ''},
  labelTop: {type: Boolean, default: false},
  labelWidth: Number,
  labelColor: {type: String, default: ''},
  requiredField: {type: Boolean, default: false},
  selectPrimitiveValue: {type: Boolean, default: false},
  disableFilter: {type: Boolean, default: false},
  useServerSearch: {type: Boolean, default: false},
  searchType: '',
  maxLength: {type: Number, default: 50},
  highlightRequiredBackground: {type: Boolean, default: false},
  transportMode: {type: String, default: ''}

})

const matches = computed(() => getSortedData());
const placeHolder = computed(() => getPlaceHolder());
const requiredFieldColor = computed(() => getRequiredFieldColor());

const activatePortIcons = computed(() => {

  if(props.searchType === 'unlocode' || props.searchType === 'iata' ) return true;

});


onMounted(() => {

  fillDropdownData();

})

watch(() => props.modelValue, () => {

  if (!props.modelValue) {

    // If model is empty, must be clear button
    selectedItem.value = props.modelValue;
    emits('update:validationFail', new Validation(0, false));

    // Stops dropdown flicker when typing custom value
  } else if (localDataSource.value.length > 0) {

    fillDropdownData();

  }

})

function fillDropdownData() {

  localDataSource.value = props.dataSource;

  // State - Set prop data from model
  if (props.modelValue) {

    if (typeof props.modelValue === 'object') {

      // Id and value supplied as object
      if (props.modelValue.id) {

        selectedItem.value = props.dataSource.find(x => x.id === props.modelValue.id).value;
        selectionMade.value = true;
        // this.readOnly = true;
      } else {
        selectedItem.value = props.modelValue.value;
      }

    } else {
      // Check if modelValue is passed as an id from dataSource items, or primitive value, update accordingly
      const getItemFromDataSource = props.dataSource.find(x => x.id === props.modelValue);

      if (getItemFromDataSource) selectedItem.value = getItemFromDataSource.value;
      if (!getItemFromDataSource) selectedItem.value = props.modelValue.trim();

      if(selectedItem.value) updateModelValue(selectedItem.value);
      // this.readOnly = true;

    }

  }
}

// Computed
function getSortedData() {

  return localDataSource.value.filter((item) => {

    if (selectedItem.length === 1) {
      return item.value.toLowerCase().startsWith(selectedItem.value.toLowerCase());

    } else {
      return item.value.toLowerCase().includes(selectedItem.value.toLowerCase());
    }

  })
}

function getPlaceHolder() {
  if (props.dataSource) {

    if (props.dataSource.length === 0 && !activatePortIcons.value) {
      return 'No data to display';
    }
  }
  return props.placeholder;
}

function getRequiredFieldColor() {
  if (props.requiredField) {
    return 'background-color: #ffeda9';
  }
  return '';
}

// End Computed


function updateValues(val) {

  val = DOMPurify.sanitize(val);

  if (props.useServerSearch) {

    searchFromServer(val)

    // return
  }

  selectionMade.value = false;

  emits('update:validationFail', new Validation(0, false));

  !props.allowCustomValue ? valueNotValid.value = !selectionMade.value : false;

  if (props.allowCustomValue) {

    if (props.selectPrimitiveValue) {

      updateModelValue(val.trim());

    } else {

      updateModelValue(new DropDownItem(props.modelValue.id, val))
    }

  } else {

    if (props.selectPrimitiveValue) {

      updateModelValue('');

    } else {

      updateModelValue(new DropDownItem(null, ''));
    }
  }


}

async function searchFromServer(searchValue) {

  if (searchValue.trim()) {

    let response;

    switch (props.searchType) {

      case 'shipper' : case 'consignee' : response = await API.searchCompanyByType(route, searchValue, props.searchType);
        break;
      case 'iata' : response = await API.getPortsByCode(props.transportMode, searchValue, true );
        break;
      case 'unlocode' : response = await API.getPortsByCode(props.transportMode, searchValue, false );
    }

    if(response) localDataSource.value = response;

  } else {

    localDataSource.value = props.dataSource;
  }

}

function updateModelValue(value) {

  if (value.hasOwnProperty('value')) return emits('update:modelValue', value);

  emits('update:modelValue', value.substring(0, props.maxLength));

}

function checkValueValid() {

  if (selectedItem.value === '') valueNotValid.value = false;
  if (!showResults.value) valueNotValid.value = !selectionMade.value && !props.allowCustomValue && selectedItem.value !== '';

}

function getSelectResultContainerPosition(popupResults) {

  nextTick(() => {

    let inputElement = inputRef.value
    let selectDrop = selectRef.value;

    let inputTopPosition = inputElement.$el.children[1].getBoundingClientRect().top;
    let inputLeftPosition = inputElement.$el.children[1].getBoundingClientRect().left;
    let inputWidth = inputElement.$el.children[1].getBoundingClientRect().width - 2;


    if (selectDrop) {

      let selectHeight = selectDrop.clientHeight + 29;
      menuFullOpenHeight.value = selectHeight

      if (!popupResults) {

        selectDrop.style.cssText = `top: ${inputTopPosition + "px"}; left: ${inputLeftPosition + "px"}; min-width: ${inputWidth + "px"} `
      } else {

        selectDrop.style.cssText = `top: ${inputTopPosition - selectHeight + "px"}; left: ${inputLeftPosition + "px"}; min-width: ${inputWidth + "px"} `
      }

    }

  })

}

function reEnterSelect() {

  // Re add this if overscroll css ever has an issue with the selectbox detaching

  // document.addEventListener("wheel", this.autoCloseSelectResults);
  //
  // if(this.showResults) {
  //   document.removeEventListener("wheel", this.closeResults);
  // }

  document.removeEventListener('wheel', closeResults)

  clearTimeout(closeTimer.value);

}

function autoCloseSelectResults(e) {

  //Measure the mouse position within the menu boundaries - close if mouse is out of bounds
  if (showResults.value) {

    let selectDrop = selectRef.value;

    let selectTop = selectDrop.offsetTop;
    let selectLeft = selectDrop.offsetLeft;
    let selectWidth = selectDrop.getBoundingClientRect().width;
    let selectHeight = selectDrop.clientHeight + 29;

    // Slight difference in value adjustment between popup and normal to stop auto close behaviour bug
    let selectBottom = selectTop + selectHeight - 32;
    let selectBottomPopup = selectTop + selectHeight - 29;

    if (!popupSelectResults.value) {

      if (e.clientX < selectLeft || e.clientX > selectLeft + selectWidth - 10 || e.pageY - window.scrollY < selectTop
          || e.pageY > selectBottom) {

        window.addEventListener("scroll", closeResults);
        document.addEventListener("wheel", closeResults);
        window.addEventListener('click', closeResults)

        // Only close if the menu is fully open
        if (selectHeight === menuFullOpenHeight.value) {

          clearTimeout(closeTimer.value)
          closeTimer.value = setTimeout(() => closeResults(), closeTime.value);

        }
      }

    } else {

      if (e.clientX < selectLeft || e.clientX > selectLeft + selectWidth - 10 || e.pageY < selectTop
          || e.pageY > selectBottomPopup) {

        window.addEventListener("scroll", closeResults);
        document.addEventListener("wheel", closeResults);

        if (selectHeight === menuFullOpenHeight.value) {

          clearTimeout(closeTimer.value)
          closeTimer.value = setTimeout(() => closeResults(), closeTime.value);
          selected.value = -1;
        }

      }

    }

  }
}

function setupClick(e) {

  // Make the select popup if the input is so far down the screen
  popupSelectResults.value = false;

  let lowerScreenArea = window.outerHeight / 1.3

  if (e.screenY > lowerScreenArea) {
    popupSelectResults.value = true;
  }

  showResults.value = true;

  getSelectResultContainerPosition(popupSelectResults.value);

  // If making the selection readonly until clear button is pressed

  // if(!this.readOnly) {
  //   this.showResults = true;
  //   this.getSelectResultContainerPosition(this.popupSelectResults);
  // }

}

function stopScroll(e) {
  if (showResults.value) e.preventDefault();
}

function scrollItems(e) {
  e.preventDefault();

  if (e.deltaY > 0) {
    selectRef.value.scrollBy(0, 22)

  } else {
    selectRef.value.scrollBy(0, -22)
  }

}

function closeResults() {

  showResults.value = false;
  selected.value = -1;

  window.removeEventListener('scroll', closeResults)
  document.removeEventListener('wheel', closeResults)
  window.removeEventListener('click', closeResults)
}

function selectItem(itemSelected, index) {

  let selectedCopy = {...itemSelected};

  emits('update:validationFail', new Validation(0, false));

  // Slight hack to cater for the concat address line 1 in the datasource (maintains the correct value for the model)
  // The copy prevents the list value being changed through reactivity
  if (itemSelected.hasOwnProperty('originValue')) {
    selectedItem.value = itemSelected.originValue;
    selectedCopy.value = itemSelected.originValue;

  } else {
    selectedItem.value = itemSelected.value;
  }

  // this.readOnly = true;
  showResults.value = false;
  selected.value = index;
  selected.value = -1;
  selectionMade.value = true;
  valueNotValid.value = false;

  emitCorrectModelValue(selectedCopy);

}

function emitCorrectModelValue(itemSelected) {

  emits('select', itemSelected)

  if (props.selectPrimitiveValue) {
    updateModelValue(itemSelected.value);
    // emits("update:modelValue", itemSelected.text);

  } else {
    updateModelValue(itemSelected);
    // emits("update:modelValue", itemSelected);
  }

}

function handleClear() {

  showResults.value = false;
  selectionMade.value = false;
  valueNotValid.value = false;
  // this.readOnly = false;
  selectedItem.value = "";
  selected.value = -1;
  emits("update:modelValue", '');
  emits('update:validationFail', new Validation(0, false));
  emits("clear");

  if (props.useServerSearch) localDataSource.value = props.dataSource;
}

function keyboardSelectItem(e) {

  // Disable any scrolling of outside div during keyboard selection
  if (e.key !== 'Tab') {
    // e.preventDefault()
    setupClick(e)
  } else {
    showResults.value = false;
  }

  // If space bar is pressed, show drop down
  if (e.key === ' ') setupClick(e)

  emits('keydown', e);

  if (e.key === 'Backspace' && selectedItem.value === "") {
    selectedItem.value = "";
    showResults.value = false;
  }

  if (e.key === 'ArrowUp' || e.key === 'Up') {

    // Prevent carrot form moving around the text box
    e.preventDefault();

    if (selected.value > 0) selected.value--;

    if (ulRef.value)
      if (ulRef.value.length > 0 && selected.value >= 0) {

        let highlightedSelectionTop = ulRef.value[selected.value].getBoundingClientRect().top;
        let selectDropTop = selectRef.value.getBoundingClientRect().top - 22; // 22 height of highlight bar

        if (highlightedSelectionTop === selectDropTop) {

          // To support IE 9 + this polyfill is required

          // npm install smoothscroll-polyfill --save

          // Add to main.js
          //import smoothscroll from 'smoothscroll-polyfill';
          //smoothscroll.polyfill();

          selectRef.value.scrollBy(0, -22)

        }

      }

  } else if (e.key === 'ArrowDown' || e.key === 'Down') {

    if (props.useServerSearch) {

      if (selected.value < localDataSource.value.length - 1) selected.value++;

    } else {
      if (selected.value < matches.value.length - 1) selected.value++;
    }

    if (selectRef.value && selected.value > 11) {
      selectRef.value.scrollBy(0, 22)

    }

  } else {

    // Check for enter key being pressed and select item
    if (e.key === 'Enter' || e.key === 'Tab') {

      if (selected.value !== -1) {
        inputRef.value.$el.children[1].blur();

        // When filtering, computed (matches array) takes the selected item text and returns one value
        // in the array, if items with the same name are present in the list the duplicates will populate the array.

        // The emit must take place first, before setting the selectedItem
        // emits("update:modelValue", JSON.parse(JSON.stringify(matches.value[selected.value])))

        if (props.useServerSearch) {

          emitCorrectModelValue(JSON.parse(JSON.stringify(localDataSource.value[selected.value])));
          selectedItem.value = localDataSource.value[selected.value].value

        } else {

          emitCorrectModelValue(JSON.parse(JSON.stringify(matches.value[selected.value])));
          selectedItem.value = matches.value[selected.value].value
        }

        readOnly.value = true;
        showResults.value = false;
        selectionMade.value = true
        valueNotValid.value = false;
        selected.value = -1;

      }

    }
  }

}

</script>

<template>
  <div id="Vue-3-component-filtered-dropdown-menu" style="position: relative;">
    <div class="value-status" :class="{'label-margin' : label}">
      <!--      <div v-if="selectionMade && allowCustomValue && !disableFilter"-->
      <!--           title="Indicates the value is from the listed items">List Item Selected-->
      <!--      </div>-->
      <!--      <div-->
      <!--          v-else-if="allowCustomValue" title="You may search the list, or enter any custom value">Custom Value-->
      <!--      </div>-->
    </div>
    <TextInput
        id="inputElement"
        ref="inputRef"
        :highlightRequiredBackground="props.highlightRequiredBackground"
        :label="label"
        :label-top="labelTop"
        :labelWidth="labelWidth"
        :label-color="labelColor"
        :requiredField="requiredField"
        :readonly="disableFilter"
        :maxLength="maxLength"
        v-model="selectedItem"
        v-model:validation-fail="validationFail"
        :validation-text="validationText"
        @click.self="setupClick"
        @keydown.stop="keyboardSelectItem"
        @input="updateValues($event.target.value)"
        @wheel="stopScroll"
        @blur="checkValueValid"
        :clearable="clearable"
        @cleared="handleClear"
        :disable="disable"
        :width="width"
        @mouseleave="autoCloseSelectResults"
        :placeholder="placeHolder">
      <div
          v-if="valueNotValid"
          class="value-status no-search-error"
          :class="{'label-margin' : label}">No Item Selected!
        <MovingTooltip :icon="false">
          <span style="color: #d45a03; font-size: 12px;">&#128712;</span>
          <template #content>
            <div style="width: 300px">No items were found within the search, or no selection was made. This is <strong>not</strong>
              a custom value and will not be saved!
            </div>
          </template>
        </MovingTooltip>
      </div>
      <div
          v-if="!selectedItem && !disable && !activatePortIcons">
        <div id="clickIcon"
             @mouseleave="autoCloseSelectResults"
             @wheel.passive="stopScroll"
             class="input-icon-container"
             @click="setupClick">
          <span class="input-arrow-down"></span>
        </div>
      </div>
    </TextInput>
    <div tabindex="0" id="select-results" ref="selectRef" @mouseleave="autoCloseSelectResults" @wheel="scrollItems"
         @mouseenter="reEnterSelect" v-if="showResults === true"
         :class="{'select-item-container' : !popupSelectResults, 'select-item-container-popup' : popupSelectResults}">
      <ul id="results-list" class="select-item-container-list" data-cy="lstDdlResult">
        <li v-if="!props.useServerSearch" ref="ulRef" v-for='(item, index) in matches' :key='item.id'
            @click="selectItem(item, index)"
            :class='{"select-highlight-current-row": selected === index}'>
          <div class="select-item-container-data" ref="listRef">
            <div class="select-list-items" :tabindex="index">{{ item.value }}</div>
          </div>
        </li>
        <li v-else ref="ulRef" v-for='(item, index) in localDataSource' :key='item.id'
            @click="selectItem(item, index)"
            :class='{"select-highlight-current-row": selected === index}'>
          <div class="select-item-container-data" ref="listRef">
            <div class="select-list-items" style="margin-right: 10px" :tabindex="index">{{ item.value }}
              <span v-if="activatePortIcons" class="select-icon">
                <span v-for="port in item.portTypes">
                   <span title="Sea Port"><img height="14" width="14" v-if="port === 'SEA'" src="@/assets/icons/mode_sea.png"></span>
                   <span title="Airport"><img height="14" width="14" v-if="port === 'AIR'" src="@/assets/icons/mode_air.png"></span>
                   <span title="Road"><img height="14" width="14" v-if="port === 'ROAD'" src="@/assets/icons/mode_road.png"></span>
                   <span title="Rail"><img height="14" width="14" v-if="port === 'RAIL'" src="@/assets/icons/mode_rail.png"></span>
                </span>
              </span>
            </div>
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<style scoped>

/*This was added to prevent other imported css from frameworks breaking the select */
/* May need to be addressed if this causes issues with sharing framework controls */
* {
  box-sizing: revert !important;
}

::-webkit-scrollbar {
  width: 12px;
}

::-webkit-scrollbar-track {
  background-color: #f6f6f6;
}

::-webkit-scrollbar-thumb {
  background: #b3b3b3;
  border-radius: 2px;
}

ul {
  font-size: 12px;
  padding: 0;
}

.input-icon-container {
  position: absolute;
  cursor: pointer;
  right: 2px;
  top: 3px;
  padding-left: 6px;
  height: 20px;
  width: 14px;
  margin-right: 2px;
  background: #d8d8d8;
  border-radius: 4px;
  font-size: 14px;
  line-height: normal;

}

.input-icon-container:hover {
  background-color: #d8d8d8;
  border-radius: 4px;
}

.input-clear-btn {
  position: absolute;
  right: 0;
  /*top: -4px;*/
  padding: 8px;
  z-index: 99;
  font-weight: bolder;
  cursor: pointer;
}

.input-clear-container {
  position: absolute;
  cursor: pointer;
  right: 2px;
  top: 3px;
  padding-left: 6px;
  height: 20px;
  width: 14px;
  margin-right: 2px;
  font-size: 14px;
  line-height: normal;
}

.input-cross {
  position: absolute;
  font-family: Arial;
  transform: rotate(45deg);
  color: #566ea0;
  font-size: 20px;
  right: 5px;
  /*top: 6px;*/
  background: none;
  user-select: none;
  -webkit-user-select: none;
  -ms-user-select: none;
}

.input-clear-btn:hover {
  opacity: 0.5;
  transition: 0.3s;
}

.input-arrow-down {
  position: absolute;
  top: 7px;
  right: 4px;
  content: "";
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 7px solid #0a0a0a;
}

.select-item-container {
  top: 0;
  position: fixed;
  margin-top: 28px;
  z-index: 999999;
  background-color: #fff;
  box-shadow: 1px 1px 4px 0px #888888;
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
  max-height: 264px;
  overflow-y: scroll;
  overscroll-behavior: contain;
  /*transform-origin: top;*/
  /*transition: transform .2s ease-in-out;*/
}

.select-item-container-popup {
  position: fixed;
  margin-top: 28px;
  z-index: 999999;
  /*bottom: -2px;*/
  /*left: 1px;*/
  /*right: 1px;*/
  background-color: #fff;
  box-shadow: 1px 1px 4px 0px #888888;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  max-height: 263px;
  overflow-y: scroll;
  overscroll-behavior: contain;
  transform-origin: bottom;
  transition: transform .2s ease-in-out;
}

.select-highlight-current-row {
  background-color: #5379ad !important;
  color: #fff;
}

.select-item-container-list {
  list-style: none;
  margin: 0;
  cursor: pointer;

}

.select-item-container-list li {
  height: 20px;
  padding: 2px 0 0 2px;
  line-height: normal;
}

.select-item-container-list li:hover {
  background-color: #dedede;
}

.select-item-container-data {
  display: grid;
  grid-template-columns: 100%;
  padding-top: 2px;
}

.value-status {
  position: absolute;
  right: 24px;
  top: 7px;
  z-index: 99;
  background-color: #ffffff;
  font-size: 12px;
  color: #42b983;
  padding-left: 4px;
  padding-right: 4px;
  border-radius: 8px;
}

.label-margin {

}

.no-search-error {
  color: #f3ba28;
  line-height: 12px;
  font-size: 11px;
}

.select-icon {
  color: var(--aqua-blue-definition);
}

.select-icon i {
  margin-left: 3px;
  float: right;
}

</style>
